import type { EquipmentItemDto, EquipmentPatch, EquipmentStatus, MyEquipmentDto } from '../../lib/api/openingTasks';
import { safeHttpUrl } from './stage-tasks';

/**
 * Спецификация оборудования в задаче «Заказ оборудования» (FB-34, 02.11.2026): чистые функции экрана партнёра —
 * прогресс, значок замены, смена статуса отметками, проверка полей до отправки, правка кеша.
 */

export const TEXT_MAX = 500;
export const URL_MAX = 1000;

export interface EquipmentProgress {
  total: number;
  /** Заказано или уже получено. */
  ordered: number;
  received: number;
  /** «заказано 3 из 10» (получено — отдельно, если есть). */
  text: string;
}

export function equipmentProgress(items: ReadonlyArray<Pick<EquipmentItemDto, 'status'>>): EquipmentProgress {
  const total = items.length;
  const ordered = items.filter((i) => i.status === 'ordered' || i.status === 'received').length;
  const received = items.filter((i) => i.status === 'received').length;
  const base = `заказано ${ordered} из ${total}`;
  return { total, ordered, received, text: received > 0 ? `${base}, получено ${received}` : base };
}

export type BadgeTone = 'orange' | 'green' | 'red';

/** Значок замены: «на согласовании УК» / «согласовано» / «отклонено: причина». Замены нет — null. */
export function replacementBadge(item: Pick<EquipmentItemDto, 'replacementText' | 'replacementStatus' | 'replacementComment'>): { text: string; tone: BadgeTone } | null {
  if (!item.replacementText || !item.replacementStatus) return null;
  if (item.replacementStatus === 'pending') return { text: 'на согласовании УК', tone: 'orange' };
  if (item.replacementStatus === 'approved') return { text: item.replacementComment ? `согласовано: ${item.replacementComment}` : 'согласовано', tone: 'green' };
  return { text: item.replacementComment ? `отклонено: ${item.replacementComment}` : 'отклонено', tone: 'red' };
}

/**
 * Новый статус после клика по отметке. «Заказано»: поставить — `ordered`, снять — `not_ordered`;
 * «Получено»: поставить — `received`, снять — назад в `ordered`. Получено подразумевает заказано.
 */
export function nextStatus(current: EquipmentStatus, mark: 'ordered' | 'received'): EquipmentStatus {
  if (mark === 'ordered') return current === 'not_ordered' ? 'ordered' : 'not_ordered';
  return current === 'received' ? 'ordered' : 'received';
}

/**
 * Позиции своей задачи (03.11.2026): у каждой позиции свой этап — задача карточки, созданная из задачи шаблона
 * («кассовое оборудование», «оборудование кухни»…). Позиция без этапа (`taskId` пуст) показывается в задаче «Заказ
 * оборудования» (`data.taskId`, как раньше). Нет позиций — null (в задаче блока нет). Возвращает копию с отфильтрованным списком;
 * сводка в ней — по всей карточке, прогресс задачи считайте по `items` (`equipmentProgress`).
 */
export function equipmentForTask(data: MyEquipmentDto | undefined, taskId: string): MyEquipmentDto | null {
  if (!data) return null;
  const items = data.items.filter((i) => (i.taskId ?? data.taskId) === taskId);
  if (items.length === 0) return null;
  return items.length === data.items.length ? data : { ...data, items };
}

/** Цена из поля ввода: пусто — `null`; «1 500,5» → «1500.5»; не число или меньше нуля — текст ошибки. */
export function normalizePrice(raw: string): { value: string | null } | { error: string } {
  const s = raw.trim().replace(/\s/g, '').replace(',', '.');
  if (!s) return { value: null };
  if (!/^\d+(\.\d{1,2})?$/.test(s)) return { error: 'Цена — число не меньше нуля, до двух знаков' };
  return { value: s };
}

/** Ссылка из поля ввода: пусто — `null`; только http(s), до 1000 символов. */
export function normalizeUrl(raw: string): { value: string | null } | { error: string } {
  const s = raw.trim();
  if (!s) return { value: null };
  if (s.length > URL_MAX) return { error: `Ссылка длиннее ${URL_MAX} символов` };
  if (!safeHttpUrl(s)) return { error: 'Нужна ссылка, начинающаяся с http:// или https://' };
  return { value: s };
}

/** Что сейчас набрано в полях позиции (все строки). */
export interface EquipmentDraft {
  replacementText: string;
  url: string;
  price: string;
  orderedAt: string;
  etaDate: string;
}

export function draftOf(item: EquipmentItemDto): EquipmentDraft {
  return {
    replacementText: item.replacementText ?? '',
    url: item.url ?? '',
    price: item.price ?? '',
    orderedAt: item.orderedAt ?? '',
    etaDate: item.etaDate ?? '',
  };
}

/**
 * Что изменилось в полях относительно сохранённого → тело запроса (только изменённые поля). Ошибка в любом поле —
 * текст ошибки, ничего не отправляем. Ничего не изменилось — пустое тело.
 */
export function patchFromDraft(item: EquipmentItemDto, draft: EquipmentDraft): { patch: EquipmentPatch } | { error: string } {
  const was = draftOf(item);
  const patch: EquipmentPatch = {};
  if (draft.replacementText.trim() !== was.replacementText.trim()) {
    const t = draft.replacementText.trim();
    if (t.length > TEXT_MAX) return { error: `Замена: не длиннее ${TEXT_MAX} символов` };
    patch.replacementText = t || null;
  }
  if (draft.url.trim() !== was.url.trim()) {
    const r = normalizeUrl(draft.url);
    if ('error' in r) return { error: r.error };
    patch.url = r.value;
  }
  if (draft.price.trim() !== was.price.trim()) {
    const r = normalizePrice(draft.price);
    if ('error' in r) return { error: r.error };
    patch.price = r.value;
  }
  if (draft.orderedAt !== was.orderedAt) patch.orderedAt = draft.orderedAt || null;
  if (draft.etaDate !== was.etaDate) patch.etaDate = draft.etaDate || null;
  return { patch };
}

/** Кеш спецификации с обновлённой позицией (ответ сервера). */
export function withEquipmentItem(data: MyEquipmentDto, updated: EquipmentItemDto): MyEquipmentDto {
  const items = data.items.map((i) => (i.id === updated.id ? updated : i));
  const p = equipmentProgress(items);
  return {
    ...data,
    items,
    summary: { total: p.total, ordered: p.ordered, received: p.received, replacementsPending: items.filter((i) => i.replacementStatus === 'pending').length },
  };
}
